import React, { useCallback, useEffect } from 'react';
import { Alert, Box, Grid } from 'housecall-ui';

import { trackEvent } from '../../hooks/useEventTracking';
import { useUploadPhoto } from '../../hooks/useUploadPhoto';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { getItemFromCart } from '../../state/utils';

import { IMAGE_LIMIT } from './consts';
import ServicePhotoPreview from './ServicePhotoPreview';
import UploadPhotosInput from './UploadPhotosInput';

type Props = {
  cartItemUUID: string;
};

const SHOW_ALERT_EVENT_NAME = 'Upload photos limit reached';

const UploadedPhotos = ({ cartItemUUID }: Props) => {
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const { service, pricingForm, cart, token } = state;
  const { uploadPhoto } = useUploadPhoto({ cartItemUUID, token });

  const cartItem = cart.find((item) => getItemFromCart(item).uuid === (service?.uuid ?? pricingForm?.uuid));

  const photosToDisplay = cartItem?.photos || [];

  const photosCount = cart.reduce((prev, curr) => prev + curr.photos.length, 0);

  useEffect(() => {
    if (photosCount === IMAGE_LIMIT) {
      trackEvent(SHOW_ALERT_EVENT_NAME);
    }
  }, [photosCount]);

  const onChange = useCallback(
    async (photos: File[]) => {
      photos.forEach((photo) => {
        dispatch(
          actions.addPhoto(cartItemUUID, {
            isLoading: true,
            file: photo,
          })
        );
        uploadPhoto(photo);
      });
    },
    [dispatch, actions, cartItemUUID, uploadPhoto]
  );

  return (
    <>
      {photosCount === IMAGE_LIMIT ? (
        <Box mb={2}>
          <Alert severity="warning">You&apos;ve reached the maximum limit of 10 pictures per booking</Alert>
        </Box>
      ) : (
        <Box mb={2}>
          <UploadPhotosInput onChange={onChange} photos={photosToDisplay} />
        </Box>
      )}
      {photosToDisplay && (
        <Grid item xs={12}>
          {Array.from(photosToDisplay).map((photo) => (
            <Box my={2} key={photo.file.name}>
              <ServicePhotoPreview cartItemUUID={cartItemUUID} photo={photo} />
            </Box>
          ))}
        </Grid>
      )}
    </>
  );
};

export default UploadedPhotos;
